import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, Alert } from '@mui/material';
import axios from '../utils/api';
import { EDIT_TOKEN_ENTRY_DESCRIPTION } from '../utils/constants';

const ServiceProviderEditToken: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';
  const tokenFromUrl = searchParams.get('token') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [token, setToken] = useState(tokenFromUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    setError(null);
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }
    if (!token.trim()) {
      setError('Token is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/service-providers/verify-edit-token', { email: email.trim(), token: token.trim() });
      navigate(`/register/agency/dashboard?email=${encodeURIComponent(email.trim())}&token=${encodeURIComponent(token.trim())}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Invalid passcode. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailFromUrl && tokenFromUrl) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Enter Passcode
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          {EDIT_TOKEN_ENTRY_DESCRIPTION}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleVerify(); }}>
          <TextField
            fullWidth label="Email Address" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required
            disabled={loading || !!tokenFromUrl} sx={{ mb: 3 }}
          />
          <TextField
            fullWidth label="Edit Passcode" value={token}
            onChange={(e) => setToken(e.target.value)} required
            disabled={loading || !!tokenFromUrl} sx={{ mb: 3 }}
            helperText="Enter the passcode from your email"
            inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '2px' } }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button type="submit" variant="contained" size="large" disabled={loading || !!tokenFromUrl} fullWidth sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {loading || tokenFromUrl ? 'Verifying...' : 'Verify Passcode'}
            </Button>
            <Button variant="outlined" size="large" component={Link} to="/register/agency/edit-request" disabled={loading || !!tokenFromUrl} fullWidth sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Request New Passcode
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Didn't receive a passcode? <Link to="/register/agency/edit-request">Request a new one</Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ServiceProviderEditToken;
